import { useEffect, useRef } from "react";
import { createSpotlightPointer } from "./spotlightPointer";

/** Cursor-following light for an existing Card; geometry belongs to the Card. */
export function CardSpotlight() {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const overlay = ref.current;
    const card = overlay?.parentElement;
    if (!overlay || !card) return;
    const finePointer = window.matchMedia("(any-hover: hover) and (any-pointer: fine)");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const pointer = createSpotlightPointer({
      requestFrame: (callback) => requestAnimationFrame(callback),
      cancelFrame: (id) => cancelAnimationFrame(id),
      bounds: () => card.getBoundingClientRect(),
      paint: (x, y) => {
        overlay.style.setProperty("--spotlight-x", `${x}px`);
        overlay.style.setProperty("--spotlight-y", `${y}px`);
      },
    });
    const reset = () => {
      pointer.reset();
      overlay.style.removeProperty("--spotlight-x");
      overlay.style.removeProperty("--spotlight-y");
    };
    // Keep the last position while CSS fades the light out; resetting it here
    // would snap the still-visible glow and border back to the card's centre.
    const leave = () => pointer.reset();
    const move = (event: PointerEvent) => {
      if (event.pointerType === "touch" || !finePointer.matches || reducedMotion.matches) return;
      pointer.move(event.clientX, event.clientY);
    };
    card.addEventListener("pointermove", move);
    card.addEventListener("pointerleave", leave);
    card.addEventListener("pointercancel", leave);
    finePointer.addEventListener("change", reset);
    reducedMotion.addEventListener("change", reset);
    return () => {
      pointer.dispose();
      card.removeEventListener("pointermove", move);
      card.removeEventListener("pointerleave", leave);
      card.removeEventListener("pointercancel", leave);
      finePointer.removeEventListener("change", reset);
      reducedMotion.removeEventListener("change", reset);
    };
  }, []);
  return <span ref={ref} aria-hidden="true" className="card-spotlight" />;
}
